import { Typography } from '@euler/app/components/typography';
import { useConfirmationAlert } from '@euler/app/tv/components/Alert';
import { FloatingModalScreen } from '@euler/app/tv/components/FloatingModalScreen';
import { tvModalScreenOptions } from '@euler/app/tv/functions/utils';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  UpdateProgressEvent,
  useCheckAppDistUpdates,
  useDownloadAndInstallUpdates,
} from '@euler/functions/useCheckAppDistUpdates';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import * as Updates from 'expo-updates';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ScrollView, TouchableOpacity, View } from 'react-native';

type LogEntry = {
  text: string;
  level?: 'log' | 'error';
  action?: string;
  onPress?: () => void;
};

export const UpdateScreen = wrapNavigatorScreen(() => {
  const theme = usePresentationTheme();
  const { rpx } = useResponsiveContext();
  const [logs, setLogs] = useState<LogEntry[]>([{ text: '正在检查更新... ' }]);
  const scrollViewRef = useRef<ScrollView>(null);
  const checkUpdates = useCheckAppDistUpdates();
  const confirmAsync = useConfirmationAlert();

  const appendLog = useCallback(
    (
      log: string,
      options?: {
        level?: 'log' | 'error';
        action?: string;
        onPress?: () => void;
      },
    ) => {
      setLogs(x => [...x, { text: log, ...options }]);
    },
    [],
  );

  const onProgress = useCallback(
    (e: UpdateProgressEvent) => {
      switch (e.type) {
        case 'initializing':
          appendLog('正在初始化... ');
          break;
        case 'download-begin':
          appendLog('开始下载... ');
          break;
        case 'download-progress':
          setLogs(x => [
            ...x.slice(0, x.length - 1),
            { text: `正在下载... ${(e.progress * 100).toFixed(0)}%` },
          ]);
          break;
        case 'download-error':
          appendLog(`下载失败: ${e.error?.message ?? '网络异常'}`, {
            level: 'error',
          });
          break;
        case 'download-complete':
          appendLog('下载完成，正在安装... ');
          break;
        case 'install-error':
          appendLog(`启动系统安装程序失败: ${e.error?.message ?? '未知错误'}`, {
            level: 'error',
          });
          break;
      }
    },
    [appendLog],
  );

  const downloadAndInstallUpdates = useDownloadAndInstallUpdates(onProgress);

  useEffect(() => {
    checkUpdates()
      .then(async result => {
        if (result == null) {
          appendLog('已经是最新版本!');
          return;
        }

        const confirmInstallBinaryUpdate = async (
          version: string,
          changeLog: string,
        ) => {
          return await confirmAsync({
            title: '发现新版本',
            message: `有最新版本${version}可更新：\n${changeLog}`,
            confirmButtonText: '下载并安装最新版本',
            cancelButtonText: '稍后再说',
          });
        };

        const confirmInstallOtaUpdates = async () => {
          return await confirmAsync({
            title: '发现OTA更新',
            message: `有最新OTA更新升级包可用，是否现在下载并升级?`,
            confirmButtonText: '下载并升级',
            cancelButtonText: '稍后再说',
          });
        };

        // procedure that download and install ota updates
        const downloadAndInstallOtaUpdates = async () => {
          try {
            appendLog('正在下载OTA更新升级包，下载完成将自动重启... ');

            const newUpdate = await Updates.fetchUpdateAsync();
            if (!newUpdate.isNew) {
              appendLog('该OTA升级包不是最新的，跳过更新');
              return;
            }
            await Updates.reloadAsync();
          } catch (e) {
            appendLog(`下载OTA升级包失败: ${(e as Error).message}`, {
              level: 'error',
              action: '重新下载',
              onPress: downloadAndInstallOtaUpdates,
            });
          }
        };

        if (result.type === 'binary') {
          // remember the downloaded file, we don't want to waste the user's
          // band width to download it again.
          let downloadedFile: string | undefined = undefined;

          // the procedure that download (if not) and install the binary update
          const downloadAndInstallBinaryUpdate = async () => {
            try {
              const res = await downloadAndInstallUpdates(
                result.head,
                downloadedFile,
              );

              // this usually occurred if the system does not permit installing
              // packages from unknown sources unless the user granted that.
              if (res.type === 'continue') {
                downloadedFile = res.downloadedFile;
                appendLog('无法安装更新，需要系统权限，请在授权后点击继续', {
                  level: 'error',
                  action: '继续安装',
                  onPress: downloadAndInstallBinaryUpdate,
                });
              } else if (res.type === 'error') {
                appendLog(
                  `安装失败: ${
                    res.message ?? res.error?.message ?? '未知错误'
                  }`,
                );
              }
            } catch (e) {
              appendLog((e as Error).message, { level: 'error' });
            }
          };

          appendLog(`发现新版本: ${result.head.version}`);

          if (
            await confirmInstallBinaryUpdate(
              result.head.version,
              result.head.changeLogContents,
            )
          ) {
            await downloadAndInstallBinaryUpdate();
          } else if (
            result.ota.available &&
            (await confirmInstallOtaUpdates())
          ) {
            // if binary update is rejected, try to install ota updates if available
            await downloadAndInstallOtaUpdates();
          }
        } else {
          appendLog(`发现可用OTA更新: ${result.manifest?.createdAt ?? '***'}`);

          if (await confirmInstallOtaUpdates()) {
            await downloadAndInstallOtaUpdates();
          }
        }
      })
      .catch(err => {
        appendLog(`检查更新失败: ${err.message}`, { level: 'error' });
      });
  }, [appendLog, checkUpdates, confirmAsync, downloadAndInstallUpdates]);

  return (
    <FloatingModalScreen>
      <ScrollView
        ref={scrollViewRef}
        onContentSizeChange={() => scrollViewRef.current?.scrollToEnd()}
        contentContainerStyle={{
          padding: rpx(30),
        }}
        css={`
          height: ${rpx(550)}px;
        `}
      >
        {logs.map((log, index) => (
          <View
            key={index}
            css={`
              border-bottom-width: 0.5px;
              border-bottom-color: ${theme.debugPanel.logSeparatorColor};
              padding: ${rpx(10)}px 0;
            `}
          >
            <HStack justifyContent="flex-start">
              <Typography.Label
                color={
                  log.level === 'error'
                    ? theme.debugPanel.logErrorTextColor
                    : theme.debugPanel.logTextColor
                }
                size={rpx(18)}
                lineHeight={rpx(24)}
                css={`
                  flex: 1;
                `}
              >
                {log.text}
              </Typography.Label>
              {log.action != null && log.onPress != null ? (
                <TouchableOpacity
                  onPress={log.onPress}
                  css={`
                    margin-left: ${rpx(6)}px;
                  `}
                >
                  <Typography.Label
                    size={rpx(18)}
                    lineHeight={rpx(24)}
                    color={theme.debugPanel.logActionTextColor}
                  >
                    {log.action}
                  </Typography.Label>
                </TouchableOpacity>
              ) : null}
            </HStack>
          </View>
        ))}
      </ScrollView>
    </FloatingModalScreen>
  );
}, tvModalScreenOptions);
